export type Me = {
  id: string;
  /** Heron account UUID (hemonea / Herald balances key). */
  heronSub?: string | null;
  displayName: string;
  username?: string | null;
  globalRole: string;
  avatarUrl?: string | null;
  avatarEmoji?: string;
  email?: string;
  hasKey?: boolean;
};

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw Object.assign(new Error(err.error || `http_${res.status}`), {
      status: res.status,
      body: err,
    });
  }
  return res.json() as Promise<T>;
}

export const hieraApi = {
  me: () => api<Me>("/api/me"),
  patchMe: (body: { displayName?: string; username?: string; email?: string; avatarEmoji?: string }) =>
    api<Me>("/api/me", { method: "PATCH", body: JSON.stringify(body) }),
  sessions: () =>
    api<{ sessions: Array<{ id: string; device: string; lastSeen: string; current: boolean }> }>("/api/me/sessions"),
  revokeSession: (id: string) =>
    api<{ ok: boolean }>(`/api/me/sessions/${id}/revoke`, { method: "POST" }),
  revokeOtherSessions: () =>
    api<{ ok: boolean }>("/api/me/sessions/revoke-others", { method: "POST" }),
  issueKey: () => api<{ key: string }>("/api/me/key", { method: "POST" }),
  people: () =>
    api<{
      people: Array<{
        id: string;
        heronSub?: string | null;
        displayName: string;
        username?: string | null;
        online: boolean;
        lastSeen: string | null;
        email?: string;
        role?: string;
        avatarUrl?: string | null;
        avatarEmoji?: string;
      }>;
    }>("/api/people"),
  siteSettings: () =>
    api<{ registrationClosed: boolean; canEdit: boolean }>("/api/site-settings"),
  patchSiteSettings: (body: { registrationClosed: boolean }) =>
    api<{ registrationClosed: boolean }>("/api/site-settings", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  createInvite: (body?: { days?: number; maxUses?: number }) =>
    api<{ token: string; expiresAt: string; maxUses: number }>("/api/invites", {
      method: "POST",
      body: JSON.stringify(body || {}),
    }),
  home: () =>
    api<{
      spaces: Array<{
        id: string;
        slug: string;
        title: string;
        icon: string | null;
        page_count: number;
        member_count: number;
        updated_at: string;
      }>;
      recent: Array<{
        id: string;
        title: string;
        icon: string | null;
        updated_at: string;
        created_at: string;
        space_id: string;
        space_title: string;
        space_slug: string;
        space_icon: string | null;
        status: string;
        editor: string | null;
        editor_avatar: string | null;
        version: number;
      }>;
      favorites: Array<Record<string, unknown>>;
    }>("/api/home"),
  agent: (messages: Array<{ role: "user" | "assistant"; content: string }>) =>
    api<{
      reply: string;
      choices: Array<{ id: string; label: string }>;
      links: Array<{ href: string; title: string; space: string }>;
      changed: boolean;
    }>("/api/agent", { method: "POST", body: JSON.stringify({ messages }) }),
  spaces: () => api<{ spaces: Array<Record<string, unknown>> }>("/api/spaces"),
  createSpace: (title: string) =>
    api<{ space: { id: string; slug: string; title: string } }>("/api/spaces", {
      method: "POST",
      body: JSON.stringify({ title }),
    }),
  space: (id: string) =>
    api<{ space: Record<string, unknown>; role: string }>(`/api/spaces/${id}`),
  patchSpace: (
    id: string,
    body: {
      title?: string;
      slug?: string;
      description?: string;
      icon?: string;
      shareDisabled?: boolean;
      viewerComments?: boolean;
    },
  ) =>
    api<{ space: Record<string, unknown>; role: string }>(`/api/spaces/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteSpace: (id: string, title: string) =>
    api<{ ok: boolean }>(`/api/spaces/${id}`, { method: "DELETE", body: JSON.stringify({ title }) }),
  exportSpace: async (id: string, filename: string) => {
    const res = await fetch(`/api/spaces/${id}/export`, { credentials: "same-origin" });
    if (!res.ok) throw new Error("export");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },
  members: (id: string) =>
    api<{ members: Array<{ userId: string; displayName: string; role: string }> }>(`/api/spaces/${id}/members`),
  searchUsers: (id: string, q: string) =>
    api<{ users: Array<{ id: string; displayName: string }> }>(
      `/api/spaces/${id}/user-search?q=${encodeURIComponent(q)}`,
    ),
  addMember: (id: string, body: { userId: string; role: string }) =>
    api<{ ok: boolean }>(`/api/spaces/${id}/members`, { method: "POST", body: JSON.stringify(body) }),
  removeMember: (id: string, userId: string) =>
    api<{ ok: boolean }>(`/api/spaces/${id}/members/${userId}`, { method: "DELETE" }),
  pages: (spaceId: string) =>
    api<{
      pages: Array<{
        id: string;
        parent_id: string | null;
        title: string;
        icon: string | null;
        status: string;
        sort_order: number;
        updated_at: string;
        created_at?: string;
        created_by?: string | null;
        updated_by?: string | null;
      }>;
      role: string;
    }>(`/api/spaces/${spaceId}/pages`),
  createPage: (spaceId: string, body: { title?: string; parentId?: string | null }) =>
    api<{ page: Record<string, unknown> }>(`/api/spaces/${spaceId}/pages`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  page: (id: string) =>
    api<{ page: PageRow; role: string; draft: Draft | null; tags: PageTag[] }>(`/api/pages/${id}`),
  patchPage: (id: string, body: Record<string, unknown>) =>
    api<{ page: PageRow }>(`/api/pages/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  movePage: (
    id: string,
    body: {
      spaceId?: string;
      parentId: string | null;
      beforeId?: string | null;
      afterId?: string | null;
    },
  ) =>
    api<{ page: PageRow }>(`/api/pages/${id}/move`, { method: "POST", body: JSON.stringify(body) }),
  copyPage: (
    id: string,
    body: { spaceId: string; parentId?: string | null; titleSuffix?: string },
  ) =>
    api<{ page: PageRow }>(`/api/pages/${id}/copy`, { method: "POST", body: JSON.stringify(body) }),
  deletePage: (id: string) => api<{ ok: boolean }>(`/api/pages/${id}`, { method: "DELETE" }),
  revisions: (id: string) =>
    api<{ revisions: Array<{ id: string; title: string; created_at: string; editor?: string }> }>(
      `/api/pages/${id}/revisions`,
    ),
  spaceTags: (spaceId: string) => api<{ tags: PageTag[] }>(`/api/spaces/${spaceId}/tags`),
  addPageTag: (pageId: string, name: string) =>
    api<{ tag: PageTag }>(`/api/pages/${pageId}/tags`, {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  removePageTag: (pageId: string, tagId: string) =>
    api<{ ok: boolean }>(`/api/pages/${pageId}/tags/${tagId}`, { method: "DELETE" }),
  createShare: (id: string, minutes: number) =>
    api<{ token: string; minutes: number; expiresAt: string; shareId?: string | null }>(
      `/api/pages/${id}/share`,
      { method: "POST", body: JSON.stringify({ minutes }) },
    ),
  share: (token: string) =>
    api<{ page: { id: string; title: string; body: unknown }; ttl: number }>(`/api/share/${token}`),
  spaceShares: (spaceId: string) =>
    api<{
      shares: Array<{
        id: string;
        token: string;
        page_id: string;
        page_title: string;
        created_at: string;
        expires_at: string;
        created_by_name: string;
      }>;
    }>(`/api/spaces/${spaceId}/shares`),
  revokeShare: (spaceId: string, shareId: string) =>
    api<{ ok: boolean }>(`/api/spaces/${spaceId}/shares/${shareId}`, { method: "DELETE" }),
  favorite: (pageId: string) =>
    api<{ ok: boolean }>("/api/favorites", { method: "POST", body: JSON.stringify({ pageId }) }),
  unfavorite: (pageId: string) =>
    api<{ ok: boolean }>("/api/favorites", { method: "DELETE", body: JSON.stringify({ pageId }) }),
  saveDraft: (id: string, body: { title?: string; body?: unknown }) =>
    api<{ ok: boolean }>(`/api/pages/${id}/draft`, { method: "PUT", body: JSON.stringify(body) }),
  search: (q: string) =>
    api<{ results: Array<Record<string, unknown>> }>(`/api/search?q=${encodeURIComponent(q)}`),
  templates: () =>
    api<{
      templates: Array<{
        id: string;
        name: string;
        nameRu?: string;
        description?: string;
        descriptionRu?: string;
        pageCount?: number;
        builtin?: boolean;
      }>;
    }>("/api/templates"),
  template: (id: string) =>
    api<{
      template: {
        id: string;
        name: string;
        nameRu?: string;
        description?: string;
        descriptionRu?: string;
        builtin?: boolean;
        pages: Array<{ rel: string; title: string; parentRel: string | null }>;
      };
    }>(`/api/templates/${encodeURIComponent(id)}`),
  applyTemplate: (spaceId: string, templateId: string) =>
    api<{ ok: boolean; pageCount: number; rootId: string | null }>(
      `/api/spaces/${spaceId}/apply-template`,
      { method: "POST", body: JSON.stringify({ templateId }) },
    ),
  importDocmost: async (files: File[], title: string) => {
    const fd = new FormData();
    if (title) fd.append("title", title);
    for (const file of files) {
      const rel = file.webkitRelativePath || file.name;
      fd.append("files", file, rel);
    }
    const res = await fetch("/api/import/docmost", {
      method: "POST",
      credentials: "same-origin",
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `import_${res.status}`);
    }
    return res.json() as Promise<{ space: { id: string; title: string }; pageCount: number }>;
  },
  upload: async (file: File, meta: { spaceId?: string; pageId?: string }) => {
    const fd = new FormData();
    fd.append("file", file);
    if (meta.spaceId) fd.append("spaceId", meta.spaceId);
    if (meta.pageId) fd.append("pageId", meta.pageId);
    const res = await fetch("/api/uploads", { method: "POST", credentials: "same-origin", body: fd });
    if (!res.ok) throw new Error(`upload_${res.status}`);
    return res.json() as Promise<{ asset: { public_url: string; kind: string; content_type: string } }>;
  },
};

export type PageTag = { id: string; name: string };

export type PageRow = {
  id: string;
  space_id: string;
  parent_id: string | null;
  title: string;
  icon?: string | null;
  body: unknown;
  status: string;
  updated_at: string;
  created_at?: string;
  deleted_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  created_by_name?: string | null;
  updated_by_name?: string | null;
};

export type Draft = {
  body?: unknown;
  title?: string;
  baseUpdatedAt?: string;
  savedAt?: string;
};
